/* HOYA hand-off to Shopify's checkout, shared by the cart page (assets/hoya-delivery.js) and the cart drawer.
   Through the Storefront API it tells Shopify how this cart is delivered (pickup at the HOYA location, or shipping
   with the address or city the shopper gave), so checkout opens with that choice already filled in. */
(() => {
  if (window.HoyaCheckoutHandoff) return;

  // Requests use the shared helper's timeouts and retries (assets/hoya-zone-terms.js loads first).
  const { requestJson } = window.HoyaZoneTerms;

  const ENDPOINT = '/api/2026-04/graphql.json';
  const readQuery = 'query HoyaCartDelivery($id: ID!) @inContext(language: HE, country: IL) { cart(id: $id) { checkoutUrl delivery { addresses { id selected address { ... on CartDeliveryAddress { address1 address2 city zip } } } } } }';
  const addMutation = 'mutation HoyaCartAddressAdd($id: ID!, $addresses: [CartSelectableAddressInput!]!, $buyer: CartBuyerIdentityInput!) @inContext(language: HE, country: IL) { addr: cartDeliveryAddressesAdd(cartId: $id, addresses: $addresses) { cart { checkoutUrl delivery { addresses { id selected } } } userErrors { field message } } buyer: cartBuyerIdentityUpdate(cartId: $id, buyerIdentity: $buyer) { cart { checkoutUrl } userErrors { field message } } }';
  const updateMutation = 'mutation HoyaCartAddressUpdate($id: ID!, $addresses: [CartSelectableAddressUpdateInput!]!, $buyer: CartBuyerIdentityInput!) @inContext(language: HE, country: IL) { addr: cartDeliveryAddressesUpdate(cartId: $id, addresses: $addresses) { cart { checkoutUrl delivery { addresses { id selected } } } userErrors { field message } } buyer: cartBuyerIdentityUpdate(cartId: $id, buyerIdentity: $buyer) { cart { checkoutUrl } userErrors { field message } } }';
  const buyerMutation = 'mutation HoyaCartBuyer($id: ID!, $buyer: CartBuyerIdentityInput!) @inContext(language: HE, country: IL) { cartBuyerIdentityUpdate(cartId: $id, buyerIdentity: $buyer) { cart { checkoutUrl } userErrors { field message } } }';

  const cartId = token => `gid://shopify/Cart/${token}`;
  const blank = value => String(value ?? '').trim() === '';
  // The drawer knows only the city; the cart page always sends a street with it.
  const cityOnly = address => !blank(address.city) && Object.keys(address).every(key => key === 'city' || blank(address[key]));

  async function storefront(query, variables, signal, policy = { attempts: 1, timeout: 8000 }) {
    const result = await requestJson(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      signal,
      body: JSON.stringify({ query, variables })
    }, policy);
    if (result.errors?.length || !result.data) {
      const error = new Error('PREFILL_UNAVAILABLE');
      error.addressCompatibility = !!result.errors?.length && result.errors.every(item => item.extensions?.code === 'INTERNAL_SERVER_ERROR' && item.path?.[0] === 'addr');
      throw error;
    }
    return result.data;
  }

  async function updateBuyer(id, buyer, signal) {
    const result = (await storefront(buyerMutation, { id, buyer }, signal, { attempts: 2, timeout: 6000 })).cartBuyerIdentityUpdate;
    if (result?.userErrors?.length || !result?.cart?.checkoutUrl) throw new Error('PREFILL_UNAVAILABLE');
    return result.cart.checkoutUrl;
  }

  function shippingBuyer(address) {
    return {
      countryCode: 'IL',
      preferences: { delivery: { deliveryMethod: ['SHIPPING'] } },
      deliveryAddressPreferences: [{ oneTimeUse: true, deliveryAddress: { country: 'Israel', ...address } }]
    };
  }

  // The address becomes the cart's selected delivery address, set together with the buyer's SHIPPING choice.
  async function addressHandoff(id, buyer, shippingAddress, signal) {
    const context = (await storefront(readQuery, { id }, signal)).cart;
    if (!context) throw new Error('CART_UNAVAILABLE');
    const usable = context.delivery.addresses.filter(address => address.id && !/\/\d+$/.test(address.id));
    const existing = usable.find(address => address.selected) || usable[0];
    const address = { countryCode: 'IL', ...shippingAddress };
    let result;
    try {
      result = await storefront(existing ? updateMutation : addMutation, {
        id, buyer, addresses: [{ ...(existing ? { id: existing.id } : { oneTimeUse: true }), selected: true, address: { deliveryAddress: address } }]
      }, signal);
      if (existing && result.addr?.userErrors?.length && !result.buyer?.userErrors?.length) {
        result = await storefront(addMutation, { id, buyer, addresses: [{ selected: true, oneTimeUse: true, address: { deliveryAddress: address } }] }, signal);
      }
    } catch (error) {
      if (!error.addressCompatibility) throw error;
      // Legacy Ajax carts can reject the newer address mutation internally.
      // The pinned Storefront version also accepts one-time checkout address preferences.
      return updateBuyer(id, shippingBuyer(shippingAddress), signal);
    }
    const refused = result.addr?.userErrors?.length || result.buyer?.userErrors?.length;
    // Shopify may refuse a city with no street (the drawer's case): it gets one try as a one-time preference.
    // A refused full address (the cart page's) fails here as it always did.
    if (refused && cityOnly(shippingAddress)) return updateBuyer(id, shippingBuyer(shippingAddress), signal);
    if (refused || !result.addr?.cart || !result.buyer?.cart?.checkoutUrl) throw new Error('PREFILL_UNAVAILABLE');
    return result.buyer.cart.checkoutUrl;
  }

  // Sets how this cart is delivered and returns its checkout URL, absolute and https only. Pickup goes to the HOYA
  // location; shipping sends the address (the cart page's full one, or the drawer's city alone), or, with no city,
  // only the SHIPPING choice, and checkout asks for the address.
  async function checkoutUrl(token, { mode, address, pickupHandle, signal } = {}) {
    if (!token) throw new Error('CART_UNAVAILABLE');
    const id = cartId(token);
    const pickup = mode === 'pickup';
    const buyer = {
      countryCode: 'IL',
      preferences: { delivery: pickup ? { deliveryMethod: ['PICK_UP'], pickupHandle: [pickupHandle] } : { deliveryMethod: ['SHIPPING'] } }
    };
    const url = pickup || !address || blank(address.city) ? await updateBuyer(id, buyer, signal) : await addressHandoff(id, buyer, address, signal);
    const destination = new URL(url, location.origin);
    if (destination.protocol !== 'https:') throw new Error('PREFILL_UNAVAILABLE');
    return destination.href;
  }

  // Shipping with this address as a one-time checkout preference. The cart page sets it before it asks Shopify for
  // rates: rate preparation follows the cart's delivery state, including a pickup chosen earlier.
  async function shippingPreference(token, address, { signal } = {}) {
    if (!token) throw new Error('CART_UNAVAILABLE');
    await updateBuyer(cartId(token), shippingBuyer(address), signal);
  }

  // The token the Storefront API needs to reach this same cart.
  async function cartToken({ root = '/', signal } = {}) {
    const cart = await requestJson(`${root}cart.js`, { signal }, { attempts: 2, timeout: 6000 });
    if (!cart?.token) throw new Error('CART_UNAVAILABLE');
    return cart.token;
  }

  // Cart attributes (the terms tick) and the order note; both reach the order. A missing note is left as it is.
  // `keepalive` lets the save finish even if the page leaves meanwhile (as Prestige's own cart note does).
  async function saveCart({ root = '/', attributes, note, signal, keepalive = false } = {}) {
    const payload = {};
    if (attributes) payload.attributes = attributes;
    if (note != null) payload.note = note;
    await requestJson(`${root}cart/update.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, signal, body: JSON.stringify(payload), ...(keepalive ? { keepalive: true } : {}) });
  }

  window.HoyaCheckoutHandoff = { cartToken, checkoutUrl, saveCart, shippingPreference };
})();
